import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link as MuiLink,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { addDays, addMonths, format as dateFnsFormat } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  createProduct,
  fetchCategories,
  getAttributes,
  getSingleProduct,
  updateProduct,
} from '@app/adapter/catalog-service';
import {
  getOrganizationById,
  updateOrganizationInfo,
} from '@app/adapter/organization-service';
import { isAdministrator } from '@app/administrator/utils/is_administrator';
import { useNavigateAdminDelegatable as useNavigate } from '@app/administrator/utils/useNavigateAdminDelegatable';
import { FormControlItem } from '@app/components/Catalog/FormControlItem';
import { FullTimeFeeNotice } from '@app/components/Catalog/FullTimeFeeNotice';
import { ProductWorkplaceField } from '@app/components/Catalog/ProductWorkplaceField';
import { WorkLocationFields } from '@app/components/Catalog/WorkLocationFields';
import { ProductLayout } from '@app/components/Layout/ProductLayout';
import {
  AttributeSelect,
  AttributeSelectItem,
} from '@app/components/Shared/AttributeSelect';
import { DateField } from '@app/components/Shared/DateField';
import { NumberField } from '@app/components/Shared/NumberField';
import {
  loggedInUserState,
  snackbarOpenState,
  snackbarTextState,
} from '@app/domain/app';
import {
  organization as organizationAtom,
  organizationSelector,
} from '@app/domain/organization';
import {
  defaultProductForm,
  ProductForm,
  productFormSchema,
} from '@app/schemas/catalog';
import { theme } from '@app/theme';
import {
  Category,
  CategoryParentName,
  Product,
  ProductInput,
  ProductPublicationStatus,
  ProductType,
  SupportedProductPublicationStatus,
} from '@app/types/catalog';
import { convertNewLine } from '@app/utils';
import {
  fromOverWorkConditions,
  toAnnualSalaryManYen,
  toAnnualSalaryYen,
  toOverWorkConditions,
  toProductPublication,
} from '@app/utils/catalog';
import { getMenuItems } from '@app/utils/components';
import {
  defaultProductInput,
  defaultProductValue,
  FULL_TIME_TERMS_URL,
  notRequireClinicalDepartmentName,
  productCategoryList,
  productStatusList,
} from '@app/utils/constants';
import { restoreSanitizedString } from '@app/utils/format';
import { getOrganizationCategory } from '@app/utils/organization';
import { getProductWorkplaceName } from '@app/utils/productOrganization';
import {
  getProductWorkLocationValues,
  getRestoredProductWorkLocationValues,
  toWorkLocationCustomFields,
  toWorkLocationIds,
  useSyncProductWorkLocation,
} from '@app/utils/useSyncProductWorkLocation';

function toCategoryId(categoryList: Category[]) {
  return categoryList.find(
    (c) =>
      c.name ===
      productCategoryList.find((c2) => c2.id === ProductType.FULL_TIME)?.name
  )?.id;
}

export interface ProductCreationState {
  id?: string;
  mode?: 'create' | 'copy' | 'edit';
}

export function FullTimeProductCreation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const organizationState = useRecoilValue(organizationSelector);
  const setOrganizationState = useSetRecoilState(organizationAtom);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const setSnackbarOpen = useSetRecoilState(snackbarOpenState);
  const setSnackbarText = useSetRecoilState(snackbarTextState);
  // 常勤求人の成功報酬フィーについてのモーダルを表示するかどうかの状態
  const [shouldShowTermsDialog, setShouldShowTermsDialog] = useState(false);
  const [isAgreeingTerms, setIsAgreeingTerms] = useState(false);
  const [isAgreeFeeChecked, setIsAgreeFeeChecked] = useState(false);
  const [isAgreeTermsChecked, setIsAgreeTermsChecked] = useState(false);
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  // 診療科目リストの状態
  const [clinicalDepartmentList, setClinicalDepartmentList] = useState<
    AttributeSelectItem[] | undefined
  >(undefined);
  // 仕事種別リストの状態
  const [jobTypeList, setJobTypeList] = useState<
    AttributeSelectItem[] | undefined
  >(undefined);
  // データ読み込み済みかどうかの状態
  const [isLoadData, setIsLoadData] = useState(false);
  const [isLoadingRegister, setIsLoadingRegister] = useState(false);
  const [isLoadingDraftSave, setIsLoadingDraftSave] = useState(false);
  const [
    isLoadingDraftSaveAndOpenPreview,
    setIsLoadingDraftSaveAndOpenPreview,
  ] = useState(false);
  const [productClinicalIds, setProductClinicalIds] = useState<string[]>([]);
  const notRequireDepartmentId = useMemo(
    () =>
      clinicalDepartmentList
        ?.filter(
          (clinical) => clinical.name === notRequireClinicalDepartmentName
        )
        .map((cli) => cli.id) || [],
    [clinicalDepartmentList]
  );
  const productState = useMemo(() => {
    return location.state as ProductCreationState;
  }, [location.state]);

  const { control, handleSubmit, watch, reset, setValue, clearErrors } =
    useForm<ProductForm>({
      defaultValues: {
        ...defaultProductForm,
        categoryEnum: ProductType.FULL_TIME,
        commuting: 'NoFee', // NOTE:交通費は常勤フォームでは収集しないため固定
        endPeriodStatus: 'day', // NOTE:常勤には勤務日の概念がなく、掲載期間は日付指定のみ
        ...getProductWorkLocationValues(organizationState),
      },
      mode: 'onChange',
      resolver: zodResolver(productFormSchema),
    });

  useSyncProductWorkLocation({
    isSkipped: !!productId || productState?.mode === 'copy',
    organization: organizationState,
    setValue,
  });

  /** APIに渡すための求人データに変換 */
  const convertProduct = useCallback(
    (formValue: ProductForm, status: SupportedProductPublicationStatus) => {
      const startPeriod = new Date(formValue.startPeriod);
      // NOTE:常勤には勤務日の概念がないため、掲載期間の終了日は常に日付指定
      const endPeriod = formValue.endPeriod
        ? new Date(formValue.endPeriod)
        : undefined;
      // NOTE:求人は自組織に紐づくため、組織区分の判定は必ず自組織を唯一の基準にする
      const productCategory = getOrganizationCategory(organizationState);
      const data: ProductInput = {
        ...defaultProductInput,
        additionalInformation: formValue.additionalInformation,
        attributes: [
          ...formValue.attributesClinical.map((id) => ({
            attributeId: id,
            value: clinicalDepartmentList?.find((a) => a.id === id)?.name ?? '',
          })),
          ...formValue.attributesJob.map((id) => ({
            attributeId: id,
            value: jobTypeList?.find((a) => a.id === id)?.name ?? '',
          })),
        ],
        categoryId: toCategoryId(categoryList) ?? '',
        customFields: {
          ...toWorkLocationCustomFields(formValue, organizationState),
          annualSalaryFixed: formValue.annualSalaryFixed,
          annualSalaryNoUpperLimit: formValue.annualSalaryNoUpperLimit,
          // NOTE:固定・上限無しの場合は上限を使用しないため保存しない
          annualSalaryTo:
            formValue.annualSalaryFixed || formValue.annualSalaryNoUpperLimit
              ? 0
              : toAnnualSalaryYen(formValue.annualSalaryTo),
          breakTime: formValue.breakTimeNoParticular
            ? 0
            : formValue.breakTime ?? 0,
          breakTimeNoParticular: formValue.breakTimeNoParticular,
          calculatedDairyPay: 0, // NOTE:常勤は時給/日給の概念がないため対象外
          calculatedHourlyPay: 0,
          catchphrase: formValue.catchphrase,
          commuting: formValue.commuting,
          commutingCost:
            formValue.commuting === 'UpperLimit' ||
            formValue.commuting === 'FixedFee' ||
            formValue.commuting === 'Include'
              ? formValue.commutingCost ?? 0
              : 0,
          count: formValue.count ?? 0,
          day: '',
          endTime: formValue.endTime,
          incentive: formValue.variantsDescription ?? '',
          isManagementPosition: formValue.isManagementPosition,
          orderConditions: formValue.orderConditions,
          // NOTE:人材紹介企業は自組織名ではなく手入力された勤務先(医療機関名)を掲載する
          organizationName: getProductWorkplaceName(
            formValue.workplaceName,
            organizationState
          ),
          overWorkConditions: toOverWorkConditions(formValue),
          productCategory,
          repeatDetail: '',
          repeatWeek: [],
          selection: '',
          startTime: formValue.startTime,
          workPublicHoliday: false,
        },
        description: formValue.description,
        locationIds: toWorkLocationIds(formValue, organizationState),
        name: formValue.name,
        publication: toProductPublication(startPeriod, endPeriod, status),
        variants: [
          {
            description: formValue.variantsDescription,
            price: {
              amount: toAnnualSalaryYen(formValue.annualSalaryFrom),
              currency: 'JPY',
              taxIncluded: true,
            },
            sku: 'year',
            ...(formValue.variantsId ? { id: formValue.variantsId } : {}), // NOTE:IDが既存の存在する場合はそれを更新
          },
        ],
      };
      return data;
    },
    [categoryList, clinicalDepartmentList, jobTypeList, organizationState]
  );

  /** 新規登録する */
  const registerNewProduct = useCallback(
    async (
      formValue: ProductForm,
      status: SupportedProductPublicationStatus
    ): Promise<string | void> => {
      try {
        const data = convertProduct(formValue, status);
        const result = await createProduct(data, organizationState.id);
        if (result.status !== 201) {
          throw new Error(`${result.status} ${result.statusText}`);
        }
        return status === ProductPublicationStatus.DRAFT
          ? result.data.id
          : navigate(`/products/fulltime/${result.data.id}`);
      } catch (error) {
        setSnackbarText(
          `求人の登録に失敗しました。, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      }
    },
    [
      convertProduct,
      setSnackbarText,
      setSnackbarOpen,
      organizationState.id,
      navigate,
    ]
  );

  /** 編集内容を登録する */
  const registerEditProduct = useCallback(
    async (formValue: ProductForm) => {
      if (!productId) {
        return;
      }
      try {
        const data = convertProduct(formValue, formValue.status);
        const result = await updateProduct(data, productId);
        if (result.status !== 200) {
          throw new Error(`${result.status} ${result.statusText}`);
        }
        navigate(`/products/fulltime/${productId}`);
      } catch (error) {
        setSnackbarText(
          `求人の更新に失敗しました。, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      }
    },
    [convertProduct, setSnackbarText, setSnackbarOpen, productId, navigate]
  );

  const handlePreview = async (productId: string) => {
    if (productId) {
      const url = `/product-preview/${productId}`;
      window.open(url, '_blank', 'noopener noreferrer');
    }
  };

  /** 下書き保存ボタンクリック処理 */
  const handleClickDraftSave: Parameters<typeof handleSubmit>[0] = useCallback(
    async (formValue) => {
      try {
        setIsLoadingDraftSave(true);
        if (productId) {
          await updateProduct(
            convertProduct(formValue, ProductPublicationStatus.DRAFT),
            productId
          );
        } else {
          await registerNewProduct(formValue, ProductPublicationStatus.DRAFT);
        }
      } catch (error) {
        setSnackbarText(
          `求人の登録に失敗しました。, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      } finally {
        setIsLoadingDraftSave(false);
      }
    },
    [
      productId,
      registerNewProduct,
      convertProduct,
      setSnackbarText,
      setSnackbarOpen,
    ]
  );

  /** プレビューボタンクリック処理 */
  const handleClickDraftSaveAndPreview: Parameters<typeof handleSubmit>[0] =
    useCallback(
      async (formValue) => {
        try {
          setIsLoadingDraftSaveAndOpenPreview(true);
          if (productId) {
            await updateProduct(
              convertProduct(formValue, ProductPublicationStatus.DRAFT),
              productId
            );
            void handlePreview(productId);
          } else {
            const newProductId = await registerNewProduct(
              formValue,
              ProductPublicationStatus.DRAFT
            );
            if (newProductId) {
              void handlePreview(newProductId);
            }
          }
          setValue('status', ProductPublicationStatus.DRAFT);
        } catch (error) {
          setSnackbarText(
            `求人の登録に失敗しました。, ${
              error instanceof Error ? error.message : error
            }`
          );
          setSnackbarOpen(true);
        } finally {
          setIsLoadingDraftSaveAndOpenPreview(false);
        }
      },
      [
        productId,
        registerNewProduct,
        convertProduct,
        setSnackbarText,
        setSnackbarOpen,
        setValue,
      ]
    );
  /** 登録ボタンクリック処理 */
  const handleClickRegister: Parameters<typeof handleSubmit>[0] = useCallback(
    async (formValue) => {
      setIsLoadingRegister(true);
      if (productId) {
        await registerEditProduct(formValue);
      } else {
        await registerNewProduct(formValue, 'ACTIVE');
      }
      setIsLoadingRegister(false);
    },
    [registerEditProduct, registerNewProduct, productId]
  );

  /** 診療科目または仕事種別の読み込み */
  const loadAttributes = useCallback(async () => {
    try {
      const result = await getAttributes({
        filter: {
          groupName: ['clinicalDepartment'],
        },
        pageNumber: 0,
        pageSize: 100,
      });
      if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      const clinicalList: AttributeSelectItem[] = result.data.value.map(
        (a) => ({
          group: a.items?.[0].groupName ?? '',
          id: a.id,
          name: a.items?.[0].value ?? '',
        })
      );
      setClinicalDepartmentList(clinicalList);

      const resultJob = await getAttributes({
        filter: {
          groupName: ['jobType'],
        },
        pageNumber: 0,
        pageSize: 100,
      });
      if (resultJob.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      const jobList: AttributeSelectItem[] = resultJob.data.value.map((a) => ({
        id: a.id,
        name: a.name ?? '',
      }));
      setJobTypeList(jobList);

      return [clinicalList, jobList];
    } catch (error) {
      setSnackbarText(
        `診療科目または仕事種別の取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
      return [[], []] as AttributeSelectItem[][];
    }
  }, [setSnackbarText, setSnackbarOpen]);

  /** 求人データの読み込み */
  const loadProduct = useCallback(
    async (
      clinicalList: AttributeSelectItem[],
      jobList: AttributeSelectItem[]
    ) => {
      if (!productId && !productState?.id) {
        return;
      }
      const id =
        (productState?.mode === 'copy' ? productState.id : productId) || '';
      try {
        const result = await getSingleProduct(id);
        if (result.status !== 200) {
          throw new Error(`${result.status} ${result.statusText}`);
        }
        const clinicalIds =
          result.data.attributes
            ?.filter(
              (a) =>
                clinicalList.some((i) => i.id === a.attributeId) &&
                a.value !== notRequireClinicalDepartmentName
            )
            .map((a) => a.attributeId) ?? [];
        setProductClinicalIds(clinicalIds);
        const product: Product = {
          ...defaultProductValue,
          ...result.data,
          customFields: {
            ...defaultProductValue.customFields,
            ...result.data.customFields,
          },
          ...(productState?.mode === 'copy'
            ? {
                day: undefined,
                id: productId,
                publication: {
                  publishedAt: '',
                  publishedBy: '',
                  since: dateFnsFormat(new Date(), 'yyyy/MM/dd'),
                  status: 'ACTIVE',
                  until: dateFnsFormat(
                    addDays(addMonths(new Date(), 6), -1),
                    'yyyy/MM/dd'
                  ),
                },
              }
            : undefined),
        };
        const {
          access,
          isOrgAgency,
          workAddress1,
          workAddress2,
          workCity,
          workLocationOther: isWorkLocationOther,
          workPostalCode,
          workPrefecture,
        } = getRestoredProductWorkLocationValues(product, organizationState);
        const lastVariant = product.variants?.[product.variants.length - 1];
        const formValues: ProductForm = {
          access,
          additionalInformation: restoreSanitizedString(
            product.additionalInformation ?? ''
          ),
          annualSalaryFixed: product.customFields?.annualSalaryFixed ?? false,
          annualSalaryFrom: toAnnualSalaryManYen(lastVariant?.price.amount),
          annualSalaryNoUpperLimit:
            product.customFields?.annualSalaryNoUpperLimit ?? false,
          annualSalaryTo: toAnnualSalaryManYen(
            product.customFields?.annualSalaryTo
          ),
          attributesClinical:
            product.attributes
              ?.filter((a) => clinicalList.some((i) => i.id === a.attributeId))
              .map((a) => a.attributeId) ?? [],
          attributesJob:
            product.attributes
              ?.filter((a) => jobList.some((i) => i.id === a.attributeId))
              .map((a) => a.attributeId) ?? [],
          breakTime: product.customFields?.breakTime ?? null,
          breakTimeNoParticular:
            product.customFields?.breakTimeNoParticular ?? false,
          catchphrase: product.customFields?.catchphrase ?? '',
          categoryEnum: ProductType.FULL_TIME,
          commuting: product.customFields
            ?.commuting as ProductForm['commuting'],
          commutingCost: product.customFields?.commutingCost ?? null,
          count: product.customFields?.count,
          days: [],
          description: restoreSanitizedString(product.description),
          endPeriod: product.publication?.until
            ? dateFnsFormat(new Date(product.publication?.until), 'yyyy/MM/dd')
            : '',
          endPeriodDayCount: null,
          endPeriodStatus: 'day',
          endTime: product.customFields?.endTime ?? '',
          inputMode: productState?.mode ?? 'create',
          isAllowDateSetting: true,
          isManagementPosition:
            product.customFields?.isManagementPosition ?? false,
          isNotRequireClinicalDepartment: !!product.attributes.some(
            (att) => att.value === notRequireClinicalDepartmentName
          ),
          isOrgAgency,
          name: product.name ?? '',
          orderConditions: convertNewLine(
            product.customFields?.orderConditions ?? ''
          ),
          ...fromOverWorkConditions(product.customFields?.overWorkConditions),
          repeatDetail: '',
          repeatWeek: [],
          selection: '',
          startPeriod: product.publication?.since
            ? dateFnsFormat(new Date(product.publication?.since), 'yyyy/MM/dd')
            : '',
          startTime: product.customFields?.startTime ?? '',
          status:
            (product.publication.status as ProductForm['status']) ?? 'ACTIVE',
          variantsDescription: convertNewLine(
            product.variants?.[product.variants.length - 1]?.description ?? ''
          ),
          variantsId:
            productState?.mode !== 'copy'
              ? product.variants?.[product.variants.length - 1]?.id
              : undefined, // NOTE:複製する場合は登録時に新たなIDが設定されるようにここでは何も入れない
          variantsPriceAmount: null,
          variantsSku: 'year',
          workAddress1,
          workAddress2,
          workCity,
          workLocationOther: isWorkLocationOther,
          workPostalCode,
          workPrefecture,
          workPublicHoliday: false,
          workplaceName: product.customFields?.organizationName ?? '',
        }; // NOTE:念のためレスポンスデータに存在しない項目があれば初期値を設定
        reset(formValues);
      } catch (error) {
        setSnackbarText(
          `求人の取得に失敗しました, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      }
    },
    [
      reset,
      setSnackbarText,
      setSnackbarOpen,
      productId,
      productState,
      organizationState,
    ]
  );

  /** 案件区分の読み込み */
  const loadCategories = useCallback(async () => {
    try {
      const result = await fetchCategories();
      if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      const category = result.data.value.find(
        (c) => c.name === CategoryParentName.PRO_DOCTOR
      );
      const list = category?.children ?? [];
      setCategoryList(list);
      return list;
    } catch (error) {
      setSnackbarText(
        `案件区分の取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
      return [];
    }
  }, [setSnackbarText, setSnackbarOpen]);

  useEffect(() => {
    void (async () => {
      const [clinicalList, jobList] = await loadAttributes();
      await loadCategories();
      await loadProduct(clinicalList, jobList);
      setIsLoadData(true);
    })();
  }, [loadAttributes, loadProduct, loadCategories]);

  // 新規登録時、組織がまだ常勤求人の成功報酬フィーについて同意していなければモーダルを表示する
  // ただし管理者が作成する場合は組織側の同意フローのため表示しない
  useEffect(() => {
    if (
      !productId &&
      !organizationState.customFields.fullTimeTermsAgreedAt &&
      !(loggedInUser && isAdministrator(loggedInUser.typeId))
    ) {
      setShouldShowTermsDialog(true);
    }
  }, [
    productId,
    organizationState.customFields.fullTimeTermsAgreedAt,
    loggedInUser,
  ]);

  const handleDeclineTerms = useCallback(() => {
    setShouldShowTermsDialog(false);
    navigate('/products/fulltime');
  }, [navigate]);

  const handleAgreeTerms = useCallback(async () => {
    try {
      setIsAgreeingTerms(true);
      // NOTE:customFieldsは全体を上書きするため、Recoilの古いスナップショットではなく
      // 最新の組織情報を取得してからマージする(他項目の巻き戻しを防ぐ)
      const { data: latestOrganization } = await getOrganizationById(
        organizationState.id
      );
      const { data: updatedOrganization } = await updateOrganizationInfo(
        organizationState.id,
        {
          customFields: {
            ...latestOrganization.customFields,
            fullTimeTermsAgreedAt: new Date().toISOString(),
            fullTimeTermsAgreedBy: loggedInUser?.id ?? '',
          },
        }
      );
      setOrganizationState(updatedOrganization);
      setShouldShowTermsDialog(false);
    } catch (error) {
      setSnackbarText(
        `同意内容の保存に失敗しました。, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
    } finally {
      setIsAgreeingTerms(false);
    }
  }, [
    organizationState.id,
    loggedInUser?.id,
    setOrganizationState,
    setSnackbarText,
    setSnackbarOpen,
  ]);

  // 編集、複製の場合、求人データが読み込めるまで内容を表示しない
  if ((productId || productState?.mode === 'copy') && !isLoadData) {
    return (
      <ProductLayout
        sx={{
          minWidth: 1000,
          px: 0,
        }}
      >
        {productId ? (
          <Typography variant="h5">{productId}を編集</Typography>
        ) : (
          <Typography variant="h5">新規登録</Typography>
        )}
        <CircularProgress sx={{ m: 'auto' }} />
      </ProductLayout>
    );
  }

  return (
    <form>
      <ProductLayout sx={{ minWidth: 1000, px: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
        >
          {productState?.mode === 'copy' ? (
            <Typography variant="h5">
              {productId}の複製を元に新規登録
            </Typography>
          ) : productId ? (
            <Typography variant="h5">{productId}を編集</Typography>
          ) : (
            <Typography variant="h5">新規登録</Typography>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <LoadingButton
              variant="outlined"
              color="secondary"
              size="small"
              loading={isLoadingDraftSave}
              onClick={handleSubmit(handleClickDraftSave)}
              sx={{ width: '120px' }}
            >
              下書き保存
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              color="secondary"
              size="small"
              sx={{
                width: '220px',
              }}
              loading={isLoadingDraftSaveAndOpenPreview}
              onClick={handleSubmit(handleClickDraftSaveAndPreview)}
            >
              下書き保存してプレビュー
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSubmit(handleClickRegister)}
              loading={isLoadingRegister}
              sx={{ width: '120px' }}
            >
              {productId ? '保存' : '登録'}
            </LoadingButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={3} px={2}>
          {productId && (
            <>
              <FormControlItem title="ステータス" required>
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        error={!!error}
                        displayEmpty
                        sx={{ bgcolor: theme.palette.common.white }}
                      >
                        {getMenuItems(productStatusList)}
                      </Select>
                      {error && (
                        <FormHelperText error sx={{ ml: 2 }}>
                          {error.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControlItem>
              <Divider />
            </>
          )}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography color={theme.customPalette.alert}>*</Typography>
            <Typography>は必須事項です</Typography>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Box sx={{ width: '12rem' }}>
              <FormControlItem title="案件区分" required>
                <Controller
                  name="categoryEnum"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} fullWidth disabled>
                      {getMenuItems(
                        productCategoryList.filter(
                          (c) => c.id === ProductType.FULL_TIME
                        )
                      )}
                    </Select>
                  )}
                />
              </FormControlItem>
            </Box>
            <FormControlItem title="掲載期間" required>
              <Stack direction="row" spacing={1} alignItems="center">
                <Controller
                  name="startPeriod"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateField
                      {...field}
                      inputRef={field.ref}
                      format="yyyy/MM/dd"
                      error={!!error}
                      helperText={error?.message}
                      sx={{ width: '150px' }}
                    />
                  )}
                />
                <Typography>～</Typography>
                <Typography>終了日</Typography>
                <Controller
                  name="endPeriod"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateField
                      {...field}
                      ref={undefined}
                      inputRef={field.ref}
                      format="yyyy/MM/dd"
                      error={!!error}
                      sx={{ width: '150px' }}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            </FormControlItem>
          </Stack>
          <FormControlItem
            title="求人案件名"
            required
            message={
              <Typography color={theme.palette.grey[500]}>
                管理者用の名称であり、応募する人には見えません。
              </Typography>
            }
          >
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  name="product-name"
                  error={!!error}
                  placeholder="常勤　内科ドクター募集"
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />
          </FormControlItem>
          <Divider />
          <Grid container>
            <Grid
              item
              xs={6}
              display="flex"
              flexDirection="column"
              gap={2}
              pr={2}
            >
              <FormControlItem title="キャッチコピー" required>
                <Controller
                  name="catchphrase"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      ref={undefined}
                      inputRef={field.ref}
                      error={!!error}
                      helperText={error?.message}
                      placeholder="例）駅近クリニックで常勤ドクターを募集しています！"
                      multiline
                      fullWidth
                      inputProps={{ maxLength: 50 }}
                    />
                  )}
                />
              </FormControlItem>
              <FormControlItem title="募集科目" required>
                <Controller
                  name="isNotRequireClinicalDepartment"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      {...field}
                      checked={field.value}
                      label={notRequireClinicalDepartmentName}
                      sx={{ width: 'fit-content' }}
                      control={<Checkbox />}
                      onChange={(e, checked) => {
                        if (checked) {
                          clearErrors('attributesClinical');
                        }
                        if (productId || productState?.id) {
                          setValue(
                            'attributesClinical',
                            checked
                              ? notRequireDepartmentId
                              : productClinicalIds
                          );
                        } else {
                          setValue(
                            'attributesClinical',
                            checked ? notRequireDepartmentId : []
                          );
                        }
                        field.onChange(e, checked);
                      }}
                    />
                  )}
                />
                {!watch('isNotRequireClinicalDepartment') && (
                  <Controller
                    name="attributesClinical"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AttributeSelect
                        {...field}
                        label="科目を選択"
                        list={clinicalDepartmentList}
                        error={!!error}
                        helperText={error?.message}
                        isGroup
                      />
                    )}
                  />
                )}
              </FormControlItem>

              <FormControlItem title="仕事種別" required>
                <Controller
                  name="attributesJob"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <AttributeSelect
                      {...field}
                      label="仕事種別を選択"
                      list={jobTypeList}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormControlItem>
              <Controller
                name="isManagementPosition"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    {...field}
                    checked={field.value}
                    control={<Checkbox />}
                    label="管理医師案件"
                    sx={{ width: 'fit-content' }}
                  />
                )}
              />
              <FormControlItem title="詳細情報">
                <Controller
                  name="description"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      inputRef={field.ref}
                      error={!!error}
                      minRows={2}
                      placeholder="詳細情報を記載"
                      helperText={error?.message}
                      multiline
                      fullWidth
                      inputProps={{ maxLength: 4000 }}
                    />
                  )}
                />
              </FormControlItem>
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column" gap={2}>
              <FormControlItem title="勤務先" required>
                <ProductWorkplaceField
                  control={control}
                  organization={organizationState}
                  setValue={setValue}
                  watch={watch}
                  fullWidth
                />
              </FormControlItem>
              <WorkLocationFields
                control={control}
                organization={organizationState}
                setValue={setValue}
                variant="creation"
                watch={watch}
              />
              <FormControlItem title="給与" required>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Controller
                    name="annualSalaryFrom"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <NumberField
                        {...field}
                        inputRef={field.ref}
                        error={!!error}
                        placeholder="金額を入力"
                        helperText={error?.message}
                        sx={{ width: '10rem' }}
                      />
                    )}
                  />
                  <Typography>万円</Typography>
                  {!watch('annualSalaryFixed') && (
                    <>
                      <Typography>〜</Typography>
                      <Controller
                        name="annualSalaryTo"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <NumberField
                            {...field}
                            inputRef={field.ref}
                            disabled={watch('annualSalaryNoUpperLimit')}
                            error={!!error}
                            placeholder="上限を入力"
                            helperText={error?.message}
                            sx={{ width: '10rem' }}
                          />
                        )}
                      />
                      <Typography>万円</Typography>
                    </>
                  )}
                </Stack>
                <Controller
                  name="annualSalaryFixed"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      {...field}
                      checked={field.value}
                      disabled={watch('annualSalaryNoUpperLimit')}
                      control={<Checkbox />}
                      label={
                        <Typography component="span" fontSize="15px">
                          年収固定
                          <Typography
                            component="span"
                            fontSize="12px"
                            color={theme.palette.grey[500]}
                          >
                            （年収指定の検索時に下限の年収のみ検索対象となります）
                          </Typography>
                        </Typography>
                      }
                      sx={{ width: 'fit-content' }}
                      onChange={(e, checked) => {
                        if (checked) {
                          setValue('annualSalaryNoUpperLimit', false);
                        }
                        field.onChange(e, checked);
                      }}
                    />
                  )}
                />
                <Controller
                  name="annualSalaryNoUpperLimit"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      {...field}
                      checked={field.value}
                      disabled={watch('annualSalaryFixed')}
                      control={<Checkbox />}
                      label={
                        <Typography component="span" fontSize="15px">
                          年収上限無し
                          <Typography
                            component="span"
                            fontSize="12px"
                            color={theme.palette.grey[500]}
                          >
                            {
                              '（"下限年収〜"　という表示になります　例）年収1000万〜　）'
                            }
                          </Typography>
                        </Typography>
                      }
                      sx={{ width: 'fit-content' }}
                      onChange={(e, checked) => {
                        if (checked) {
                          setValue('annualSalaryTo', null);
                        }
                        field.onChange(e, checked);
                      }}
                    />
                  )}
                />
              </FormControlItem>
            </Grid>
          </Grid>
        </Stack>
      </ProductLayout>
      <Dialog open={shouldShowTermsDialog} onClose={handleDeclineTerms}>
        <DialogTitle>常勤求人の成功報酬フィーについて</DialogTitle>
        <DialogContent sx={{ width: '32rem' }}>
          <Stack spacing={3}>
            <FullTimeFeeNotice />
            <Stack spacing={2}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Checkbox
                  checked={isAgreeFeeChecked}
                  onChange={(e, checked) => setIsAgreeFeeChecked(checked)}
                  sx={{ p: 0 }}
                />
                <Typography sx={{ pt: '2px' }}>
                  ProDoctorsを通じて常勤の採用した場合、利用料を支払うことに同意する
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Checkbox
                  checked={isAgreeTermsChecked}
                  onChange={(e, checked) => setIsAgreeTermsChecked(checked)}
                  sx={{ p: 0 }}
                />
                <Typography sx={{ pt: '2px' }}>
                  <MuiLink
                    href={FULL_TIME_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    color="text.primary"
                    underline="always"
                  >
                    利用規約
                  </MuiLink>
                  について同意する
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleDeclineTerms}
            sx={{ minWidth: '120px' }}
          >
            戻る
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() =>
              window.open(FULL_TIME_TERMS_URL, '_blank', 'noopener noreferrer')
            }
            sx={{ minWidth: '120px' }}
          >
            利用規約を表示
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            loading={isAgreeingTerms}
            disabled={!isAgreeFeeChecked || !isAgreeTermsChecked}
            onClick={handleAgreeTerms}
            sx={{ minWidth: '120px' }}
          >
            次へ
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </form>
  );
}
